import React, { useEffect, useState } from "react";
import { useForm, useSelect } from "@refinedev/antd";
import { Form, Button, DatePicker, Input, InputNumber, Select, Switch, Row, Col, Card, Space, Typography } from "antd";
import { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { MinusCircleOutlined, PlusOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;

interface TaxOption {
  label: string;
  value: number;
  data: {
    id: number;
    taxType: string;
    rate: number;
    isActive: boolean;
  };
}

const generateOrderId = (): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let orderId = '';
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    orderId += characters[randomIndex];
  }
  return orderId;
};

interface IPurchaseForm {
  Reference: string;
  TransactionDate: string;
  VendorId: string;
  SupplierCode: string;
  SupplierTPIN: string;
  CurrencyTypeCode: string;
  ExchangeRate: number;
  SubTotal: number;
  TotalTaxableAmount: number;
  TotalTax: number;
  TotalAmount: number;
  Status: string;
  IsManualVendor: boolean;
  ManualVendorName: string;
  ManualVendorTpin: string;
  ManualVendorContact: string;
  ManualVendorPhone: string;
  ManualVendorEmail: string;
  Items: Array<{
    InventoryItemId: number | null;
    ItemCode: string;
    ItemName: string;
    ItemClassCode: string;
    QuantityUnitCode: string;
    Quantity: number;
    PurchasePrice: number;
    TotalAmount?: number;
    CurrencyId?: number;
    IsManualItem: boolean;
    DefaultUnitPrice?: number;
    TaxRate?: number;
    TaxId?: number;
    TaxType?: string;
    DiscountType?: string;
    DiscountValue?: number;
  }>;
}


export const PurchaseCreate: React.FC = () => {
  const [isManualVendor, setIsManualVendor] = useState(false);
  const [taxOptions, setTaxOptions] = useState<TaxOption[]>([]);

  const { form, formProps, saveButtonProps } = useForm({
    resource: "purchases",
    meta: {
      fields: [
        "Reference",
        "TransactionDate",
        "VendorId",
        "CurrencyTypeCode",
        "ExchangeRate",
        "SubTotal",
        "TotalTaxableAmount",
        "TotalTax",
        "TotalAmount",
        "Status",
        "IsManualVendor",
        "ManualVendorName",
        "ManualVendorTpin",
        "ManualVendorContact",
        "ManualVendorPhone",
        "ManualVendorEmail",
        {
          Items: [
            "InventoryItemId",
            "ItemCode",
            "ItemName",
            "ItemClassCode",
            "QuantityUnitCode",
            "Quantity",
            "PurchasePrice",
            "CurrencyId",
            "IsManualItem",
            "DefaultUnitPrice",
            "TaxRate",
            "TaxId",
            "TaxType",
            "DiscountType",
            "DiscountValue"
          ]
        }
      ]
    },
    onMutationSuccess: (data: any) => {
      console.log('Mutation success:', data);
    },
    onMutationError: (error: any) => {
      console.error('Mutation error:', error);
    }
  });

  const { selectProps: vendorSelectProps, queryResult } = useSelect({
    resource: "vendors",
    optionLabel: "name",
    optionValue: "id",
    meta: {
      fields: ["id", "name", "tpin", "description", "email", "phone", "address"]
    },
    onSearch: (value: string) => [
      {
        field: "name",
        operator: "contains",
        value,
      },
    ],
  });

  const { data: currencies, isLoading: currenciesLoading } = useQuery({
    queryKey: ["currencies"],
    queryFn: () => api.getCurrencies(),
    onError: (error) => {
      console.error("Error fetching currencies:", error);
    }
  });

  const { selectProps: itemSelectProps, queryResult: itemQueryResult } = useSelect({
    resource: "inventory",
    optionLabel: "itemName",
    optionValue: "id",
    meta: {
      fields: [
        "id",
        "itemCode",
        "itemName",
        "itemClassificationCode",
        "quantityUnitCode",
        "barcode",
        "additionalInformation",
        "isActive",
        {
          stockLevel: [
            "id",
            "currentStockLevel",
            "availableStock"
          ]
        },
        {
          prices: [
            "id",
            "basePrice",
            "effectivePrice",
            "isActive",
            "currencyCode"
          ]
        }
      ]
    }
  });

  const { isLoading: taxesLoading } = useQuery({
    queryKey: ['taxes'],
    queryFn: () => api.getTaxes(),
    onSuccess: (data) => {
      const options = data.map((tax: any) => ({
        label: `${tax.taxType} (${tax.rate}%)`,
        value: tax.id,
        data: tax
      }));
      setTaxOptions(options);
    },
    onError: (error) => {
      console.error("Error fetching taxes:", error);
    }
  });

  const handleVendorSelect = async (value: string) => {
    const vendorData = queryResult?.data?.data?.find(
      (vendor: any) => vendor.id.toString() === value.toString()
    );

    if (vendorData) {
      form.setFieldsValue({
        SupplierCode: vendorData.id.toString(),
        SupplierTPIN: vendorData.tpin || '',
      });
    }
  };

  const handleItemSelect = async (value: string, name: number) => {
    const itemData = itemQueryResult?.data?.data?.find(
      (item: any) => item.id.toString() === value.toString()
    );

    if (itemData) {
      const currencyCode = form.getFieldValue('CurrencyTypeCode');
      const selectedCurrency = currencies?.find((c: any) => c.currencyCode === currencyCode);

      const activePrice = itemData.prices?.find((price: any) => price.isActive);
      const anyPrice = itemData.prices?.[0];
      const priceRecord = activePrice || anyPrice;
      
      const priceValue = (Number(priceRecord?.effectivePrice) > 0 ? Number(priceRecord?.effectivePrice) : Number(priceRecord?.basePrice)) || 0;

      form.setFields([
        { name: ['Items', name, 'InventoryItemId'], value: itemData.id },
        { name: ['Items', name, 'ItemCode'], value: itemData.itemCode },
        { name: ['Items', name, 'ItemName'], value: itemData.itemName },
        { name: ['Items', name, 'ItemClassCode'], value: itemData.itemClassificationCode || '' },
        { name: ['Items', name, 'QuantityUnitCode'], value: itemData.quantityUnitCode || '' },
        { name: ['Items', name, 'PurchasePrice'], value: priceValue },
        { name: ['Items', name, 'DefaultUnitPrice'], value: priceValue },
        { name: ['Items', name, 'Quantity'], value: 1 },
        { name: ['Items', name, 'TotalAmount'], value: priceValue },
        { name: ['Items', name, 'CurrencyId'], value: selectedCurrency?.id || 1 },
        { name: ['Items', name, 'IsManualItem'], value: false },
      ]);

      setTimeout(() => {
        updateLineAmounts(name);
      }, 50);
    }
  };

  const handleManualItemToggle = (checked: boolean, name: number) => {
    const currentItems = form.getFieldValue('Items') || [];

    if (checked) {
      const defaultTaxId = taxOptions.length > 0 ? taxOptions[0].value : undefined;
      const defaultTaxType = taxOptions.length > 0 ? taxOptions[0].data.taxType : 'VAT';
      const defaultTaxRate = taxOptions.length > 0 ? taxOptions[0].data.rate : 0;

      currentItems[name] = {
        ...currentItems[name],
        IsManualItem: true,
        InventoryItemId: null,
        DefaultUnitPrice: currentItems[name]?.PurchasePrice || 0,
        TaxId: defaultTaxId,
        TaxType: defaultTaxType,
        TaxRate: defaultTaxRate,
        DiscountType: 'None',
        DiscountValue: 0,
        QuantityUnitCode: 'EA',
        ItemCode: `MANUAL-${generateOrderId()}`
      };
    } else {
      currentItems[name] = {
        ...currentItems[name],
        IsManualItem: false,
        DefaultUnitPrice: undefined,
        TaxId: undefined,
        TaxType: undefined,
        DiscountType: undefined,
        DiscountValue: undefined,
        TaxRate: undefined
      };
    }

    form.setFieldsValue({
      Items: currentItems
    });
  };

  const handleCurrencyChange = async (value: string) => {
    const selectedCurrency = currencies?.find(
      (currency: any) => currency.currencyCode === value
    );

    if (selectedCurrency) {
      form.setFieldsValue({
        ExchangeRate: selectedCurrency.exchangeRate || 1.0
      });

      const currentItems = form.getFieldValue('Items') || [];
      if (currentItems.length > 0) {
        const updatedItems = currentItems.map((item: any) => ({
          ...item,
          CurrencyId: selectedCurrency.id
        }));

        form.setFieldsValue({
          Items: updatedItems
        });
      }
    } else {
      form.setFieldsValue({
        ExchangeRate: 1.0
      });
    }
  };

  const updateLineAmounts = (name: number) => {
    const currentItems = form.getFieldValue('Items') || [];
    const currentItem = currentItems[name];

    if (!currentItem) return;

    const quantity = Number(currentItem.Quantity) || 0;
    const price = Number(currentItem.PurchasePrice) || 0;

    const totalAmount = quantity * price;

    currentItems[name] = {
      ...currentItem,
      TotalAmount: totalAmount
    };

    const orderTotals = currentItems.reduce((acc: {
      SubTotal: number;
      TotalTaxableAmount: number;
      TotalTax: number;
      TotalAmount: number;
    }, item: IPurchaseForm['Items'][0]) => {
      const itemTotal = Number(item.TotalAmount) ||
        (Number(item.Quantity || 0) * Number(item.PurchasePrice || 0));
      return {
        SubTotal: acc.SubTotal + itemTotal,
        TotalTaxableAmount: acc.TotalTaxableAmount + itemTotal,
        TotalTax: 0,
        TotalAmount: acc.TotalAmount + itemTotal
      };
    }, {
      SubTotal: 0,
      TotalTaxableAmount: 0,
      TotalTax: 0,
      TotalAmount: 0
    });

    form.setFieldsValue({
      Items: currentItems,
      ...orderTotals
    });
  };

  const handleQuantityChange = (name: number) => {
    updateLineAmounts(name);
  };

  const handlePriceChange = (name: number) => {
    updateLineAmounts(name);

    const items = form.getFieldValue('Items') || [];
    const item = items[name];

    if (item && item.IsManualItem) {
      if (!item.DefaultUnitPrice || item.DefaultUnitPrice < item.PurchasePrice) {
        items[name] = {
          ...item,
          DefaultUnitPrice: item.PurchasePrice
        };

        form.setFieldsValue({
          Items: items
        });
      }
      updateEffectivePrice(name);
    }
  };

  const handleManualVendorToggle = (checked: boolean) => {
    setIsManualVendor(checked);

    if (checked) {
      form.setFieldsValue({
        VendorId: undefined,
        SupplierCode: '',
        SupplierTPIN: '',
        IsManualVendor: true
      });
    } else {
      form.setFieldsValue({
        ManualVendorName: '',
        ManualVendorContact: '',
        IsManualVendor: false
      });
    }
  };

  const updateEffectivePrice = (name: number) => {
    const currentItems = form.getFieldValue('Items') || [];
    const currentItem = currentItems[name];

    if (!currentItem || !currentItem.IsManualItem) return;

    const purchasePrice = Number(currentItem.PurchasePrice) || 0;
    const discountType = currentItem.DiscountType || 'None';
    const discountValue = Number(currentItem.DiscountValue) || 0;

    let sellingPrice = purchasePrice;

    if (discountType === 'Percentage') {
      sellingPrice = purchasePrice - (purchasePrice * (discountValue / 100));
    } else if (discountType === 'FixedAmount') {
      sellingPrice = purchasePrice - discountValue;
    }

    sellingPrice = Math.max(0, sellingPrice);

    currentItems[name] = {
      ...currentItem,
      DefaultUnitPrice: sellingPrice
    };

    form.setFieldsValue({
      Items: currentItems
    });
  };

  const handleDiscountChange = (name: number) => {
    updateEffectivePrice(name);
  };

  const handleTaxSelect = (taxId: number, name: number) => {
    const selectedTax = taxOptions.find(option => option.value === taxId);

    if (selectedTax) {
      const { taxType, rate } = selectedTax.data;

      const currentItems = form.getFieldValue('Items') || [];
      currentItems[name] = {
        ...currentItems[name],
        TaxId: taxId,
        TaxRate: rate,
        TaxType: taxType
      };

      form.setFieldsValue({ Items: currentItems });
    }
  };

  const handleSubmit = async (values: any) => {
    let supplierCode = values.SupplierCode;
    let supplierTPIN = values.SupplierTPIN;

    if (values.IsManualVendor) {
      supplierCode = 'MANUAL-' + generateOrderId();
      supplierTPIN = values.ManualVendorTpin || 'NONE';
    }

    const transformedValues = {
      reference: values.Reference,
      transactionDate: values.TransactionDate,
      supplierCode: supplierCode,
      supplierTPIN: supplierTPIN,
      currencyTypeCode: values.CurrencyTypeCode,
      exchangeRate: values.ExchangeRate,
      subTotal: values.SubTotal,
      totalTaxableAmount: values.TotalTaxableAmount,
      totalTax: values.TotalTax || 0,
      totalAmount: values.TotalAmount,
      status: values.Status,
      ...(values.IsManualVendor && {
        manualVendor: {
          name: values.ManualVendorName,
          contact: values.ManualVendorContact,
        }
      }),
      items: (values.Items || []).map((item: any) => {
        const manualItemData = item.IsManualItem ? {
          manualItem: {
            itemCode: item.ItemCode || `MANUAL-${generateOrderId()}`,
            itemName: item.ItemName,
            itemClassCode: item.ItemClassCode || 'GENERAL',
            quantityUnitCode: item.QuantityUnitCode || 'EA',
            defaultUnitPrice: item.DefaultUnitPrice || item.PurchasePrice || 0,
            taxRate: item.TaxRate || 0,
            taxId: item.TaxId,
            taxType: item.TaxType || 'VAT',
            discountType: item.DiscountType || 'None',
            discountValue: item.DiscountValue || 0
          }
        } : {};

        return {
          inventoryItemId: item.IsManualItem ? null : item.InventoryItemId,
          currencyId: item.CurrencyId,
          quantity: item.Quantity,
          purchasePrice: item.PurchasePrice,
          ...manualItemData
        };
      })
    };

    try {
      const result = await formProps.onFinish?.(transformedValues);
      console.log('Submit result:', result);
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  useEffect(() => {
    const Reference = generateOrderId();
    form.setFieldsValue({
      Reference,
      IsManualVendor: false
    });
  }, [form]);


  return (
    <div className="show-page-wrapper">
      <PageHeader
        title="Create New Purchase"
        titleIcon={<ShoppingCartOutlined />}
        breadcrumbs={[
          { title: "Purchases", href: "/purchases" },
          { title: "Create Purchase" },
        ]}
        showBack={true}
      />

      <Form
        {...formProps}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Purchase Information Section */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Purchase Information</h3>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Reference"
                  name="Reference"
                  rules={[{ required: true }]}
                >
                  <Input readOnly style={{ fontWeight: 'bold' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Transaction Date"
                  name="TransactionDate"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Form.Item hidden name="Status" initialValue="Completed">
                <Input />
              </Form.Item>
              <Form.Item hidden name="IsManualVendor" valuePropName="checked" initialValue={false}>
                <Input />
              </Form.Item>
            </Row>
          </div>
        </div>

        {/* Vendor Information Section */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Vendor Information</h3>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24}>
                <Space align="center" size={12}>
                  <Switch
                    checked={isManualVendor}
                    onChange={handleManualVendorToggle}
                    checkedChildren="New Vendor"
                    unCheckedChildren="Select Existing"
                  />
                  <Text type="secondary">Create new vendor</Text>
                </Space>
              </Col>
            </Row>

            {!isManualVendor ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Vendor"
                    name="VendorId"
                    rules={[{ required: !isManualVendor, message: 'Please select a vendor' }]}
                  >
                    <Select
                      {...vendorSelectProps}
                      onSelect={handleVendorSelect}
                      showSearch
                      placeholder="Search vendor"
                      filterOption={false}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Supplier Code"
                    name="SupplierCode"
                    rules={[{ required: !isManualVendor }]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Supplier TPIN"
                    name="SupplierTPIN"
                    rules={[{ required: !isManualVendor }]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Vendor Name"
                    name="ManualVendorName"
                    rules={[{ required: isManualVendor }]}
                  >
                    <Input placeholder="Enter vendor name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Contact" name="ManualVendorContact">
                    <Input placeholder="Enter contact" />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </div>
        </div>

        {/* Currency Information Section */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Currency Information</h3>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Currency"
                  name="CurrencyTypeCode"
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder="Select currency"
                    onChange={handleCurrencyChange}
                    loading={currenciesLoading}
                  >
                    {currencies?.map((currency: any) => (
                      <Select.Option key={currency.id} value={currency.currencyCode}>
                        {currency.currencyCode} ({currency.symbol}) - {currency.currencyName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Exchange Rate"
                  name="ExchangeRate"
                  initialValue={1}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={999999999} precision={4} style={{ width: "100%" }} disabled />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Purchase Items Section */}
        <Card title="Purchase Items" className="data-table-card" style={{ marginBottom: 16 }}>
          <Form.Item name="Items" noStyle>
            <Form.List name="Items">
              {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      style={{ marginBottom: 12 }}
                      size="small"
                      type="inner"
                      extra={
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                      }
                    >
                      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                        <Col xs={24}>
                          <Space align="center" size={12}>
                            <Switch
                              checked={form.getFieldValue(['Items', name, 'IsManualItem'])}
                              onChange={(checked) => handleManualItemToggle(checked, name)}
                              checkedChildren="New Product"
                              unCheckedChildren="Select Existing"
                            />
                            <Text type="secondary">Create new product</Text>
                          </Space>
                        </Col>
                      </Row>

                      {!form.getFieldValue(['Items', name, 'IsManualItem']) ? (
                        <Row gutter={[12, 12]}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              {...restField}
                              label="Item Code"
                              name={[name, 'InventoryItemId']}
                              rules={[{ required: !form.getFieldValue(['Items', name, 'IsManualItem']) }]}
                            >
                              <Select
                                showSearch
                                placeholder="Search item"
                                filterOption={(input: string, option: any) =>
                                  (option?.label && typeof option.label === 'string')
                                    ? option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    : false
                                }
                                onChange={(value) => handleItemSelect(value, name)}
                                options={itemQueryResult?.data?.data?.map((item: any) => ({
                                  label: `${item.itemCode} - ${item.itemName}`,
                                  value: item.id.toString()
                                }))}
                              />
                            </Form.Item>
                            <Form.Item hidden {...restField} name={[name, 'ItemCode']}><Input /></Form.Item>
                            <Form.Item hidden {...restField} name={[name, 'IsManualItem']} valuePropName="checked" initialValue={false}><Input /></Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item {...restField} label="Item Name" name={[name, 'ItemName']} rules={[{ required: true }]}>
                              <Input readOnly placeholder="Item Name" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item {...restField} label="Class Code" name={[name, 'ItemClassCode']} rules={[{ required: true }]}>
                              <Input readOnly placeholder="Class Code" />
                            </Form.Item>
                          </Col>
                        </Row>
                      ) : (
                        <>
                          <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12}>
                              <Form.Item {...restField} label="Item Name" name={[name, 'ItemName']} rules={[{ required: true }]}>
                                <Input placeholder="Enter item name" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Form.Item {...restField} label="Selling Price" name={[name, 'DefaultUnitPrice']} rules={[{ required: form.getFieldValue(['Items', name, 'IsManualItem']) }]}>
                                <InputNumber placeholder="Selling Price" min={0} max={999999999} precision={2} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...restField} label="Tax Type" name={[name, 'TaxId']}>
                                <Select
                                  placeholder="Select tax type"
                                  loading={taxesLoading}
                                  options={taxOptions}
                                  onChange={(value) => handleTaxSelect(value, name)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...restField} label="Tax Rate (%)" name={[name, 'TaxRate']} initialValue={0}>
                                <InputNumber placeholder="Tax Rate %" min={0} max={100} precision={2} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...restField} label="Discount Type" name={[name, 'DiscountType']} initialValue="None">
                                <Select
                                  options={[
                                    { label: "None", value: "None" },
                                    { label: "Percentage", value: "Percentage" },
                                    { label: "Fixed Amount", value: "FixedAmount" }
                                  ]}
                                  onChange={() => handleDiscountChange(name)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...restField} label="Discount Value" name={[name, 'DiscountValue']} initialValue={0}>
                                <InputNumber placeholder="Discount Value" min={0} max={999999999} precision={2} style={{ width: '100%' }} onChange={() => handleDiscountChange(name)} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* Shared Fields */}
                      <Row gutter={[12, 12]}>
                        <Col xs={12} sm={8} md={4}>
                          <Form.Item {...restField} label="Quantity" name={[name, 'Quantity']} rules={[{ required: true }]}>
                            <InputNumber placeholder="Qty" min={0} max={999999999} precision={2} style={{ width: '100%' }} onChange={() => handleQuantityChange(name)} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                          <Form.Item {...restField} label="Purchase Price" name={[name, 'PurchasePrice']} rules={[{ required: true }]}>
                            <InputNumber placeholder="Price" min={0} max={999999999} precision={2} style={{ width: '100%' }} onChange={() => handlePriceChange(name)} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                          <Form.Item {...restField} label="Total Amount" name={[name, 'TotalAmount']} rules={[{ required: true }]}>
                            <InputNumber readOnly placeholder="Total" min={0} max={999999999} precision={2} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item hidden {...restField} name={[name, 'CurrencyId']}>
                        <InputNumber min={0} max={999999999} />
                      </Form.Item>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Item
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Card>

        {/* Purchase Summary Section */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Purchase Summary</h3>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item label="Subtotal" name="SubTotal" rules={[{ required: true }]}>
                  <InputNumber style={{ width: "100%" }} precision={2} readOnly min={0} max={999999999} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item label="Total Taxable Amount" name="TotalTaxableAmount" rules={[{ required: true }]}>
                  <InputNumber style={{ width: "100%" }} precision={2} readOnly min={0} max={999999999} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item label={<Text strong>Total Amount</Text>} name="TotalAmount" rules={[{ required: true }]}>
                  <InputNumber style={{ width: "100%", fontWeight: 'bold', fontSize: '16px' }} precision={2} readOnly min={0} max={999999999} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" {...saveButtonProps} size="large">
            Create Purchase
          </Button>
        </div>
      </Form>
    </div>
  );
};
